import { useId, useState } from "react";
import type { FormEvent } from "react";
import {
  api,
  ApiError,
  FINDING_STATUSES,
  toApiError,
  unwrap,
  type FindingStatus,
  type StatusOut,
} from "../api/client";
import { t } from "../i18n/t";
import { formatDateTime, statusLabel } from "../lib/format";

const COMMENT_MAX = 2000;

interface Props {
  orgId: string;
  nr: string;
  findingKey: string;
  title: string;
  status: FindingStatus;
  comment: string | null;
  updatedAt?: string | null;
  /** "panel": status + comment saved together; "inline": status saves on change (table rows). */
  variant: "panel" | "inline";
  onSaved?: (out: StatusOut) => void;
}

/** Edit a finding's status and comment (PUT …/findings/{key}/status, persisted per org). */
export function StatusEditor(props: Props) {
  const { orgId, nr, findingKey, title, variant, onSaved } = props;
  const [saved, setSaved] = useState({ status: props.status, comment: props.comment ?? "" });
  const [status, setStatus] = useState<FindingStatus>(props.status);
  const [comment, setComment] = useState(props.comment ?? "");
  const [updatedAt, setUpdatedAt] = useState(props.updatedAt ?? null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState<ApiError | null>(null);
  const statusId = useId();
  const commentId = useId();

  async function save(nextStatus: FindingStatus, nextComment: string) {
    setSaving(true);
    setError(null);
    setMessage("");
    try {
      const out = await unwrap(
        api.PUT("/api/orgs/{org_id}/kommuner/{nummer}/findings/{key}/status", {
          params: { path: { org_id: orgId, nummer: nr, key: findingKey } },
          body: { status: nextStatus, comment: nextComment.trim() || null },
        }),
      );
      const persisted = { status: out.status, comment: out.comment ?? "" };
      setSaved(persisted);
      setStatus(persisted.status);
      setComment(persisted.comment);
      setUpdatedAt(out.updated_at);
      setMessage(
        variant === "panel"
          ? t("statusEditor.savedStatus", { title, status: statusLabel(out.status) })
          : t("statusEditor.saved"),
      );
      onSaved?.(out);
    } catch (err) {
      setError(toApiError(err));
      if (variant === "inline") setStatus(saved.status);
    } finally {
      setSaving(false);
    }
  }

  const dirty = status !== saved.status || comment.trim() !== saved.comment.trim();
  const commentDirty = comment.trim() !== saved.comment.trim();

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (variant === "panel" ? dirty : commentDirty) void save(status, comment);
  }

  const statusSelect = (
    <select
      id={statusId}
      value={status}
      disabled={saving}
      aria-label={variant === "inline" ? t("statusEditor.statusFor", { title }) : undefined}
      onChange={(e) => {
        const next = e.target.value as FindingStatus;
        setStatus(next);
        setMessage("");
        if (variant === "inline") void save(next, saved.comment);
      }}
    >
      {FINDING_STATUSES.map((s) => (
        <option key={s} value={s}>
          {statusLabel(s)}
        </option>
      ))}
    </select>
  );

  const feedback = (
    <>
      <p className="live-message" role="status" aria-live="polite">
        {message}
      </p>
      {error && (
        <p className="field-error" role="alert">
          {error.userMessage}
        </p>
      )}
    </>
  );

  if (variant === "inline") {
    return (
      <form className="status-inline" onSubmit={onSubmit}>
        {statusSelect}
        <input
          id={commentId}
          type="text"
          value={comment}
          maxLength={COMMENT_MAX}
          placeholder={t("statusEditor.commentPlaceholder")}
          aria-label={t("statusEditor.commentFor", { title })}
          onChange={(e) => {
            setComment(e.target.value);
            setMessage("");
          }}
        />
        {commentDirty && (
          <button type="submit" className="btn btn-primary btn-small" disabled={saving}>
            {saving ? t("statusEditor.saving") : t("statusEditor.saveComment")}
          </button>
        )}
        {feedback}
      </form>
    );
  }

  return (
    <form className="status-panel" onSubmit={onSubmit}>
      <div className="field">
        <label htmlFor={statusId}>{t("statusEditor.status")}</label>
        {statusSelect}
      </div>
      <div className="field">
        <label htmlFor={commentId}>{t("statusEditor.comment")}</label>
        <textarea
          id={commentId}
          rows={3}
          value={comment}
          maxLength={COMMENT_MAX}
          placeholder={t("statusEditor.commentPlaceholder")}
          onChange={(e) => {
            setComment(e.target.value);
            setMessage("");
          }}
        />
      </div>
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={saving || !dirty}>
          {saving ? t("statusEditor.saving") : t("statusEditor.save")}
        </button>
        {updatedAt && (
          <span className="muted">
            {t("statusEditor.lastUpdated", { date: formatDateTime(updatedAt) })}
          </span>
        )}
      </div>
      {feedback}
    </form>
  );
}
